import React, { useState, useEffect } from 'react';
import { teamsAPI, usersAPI, kpisAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  UsersRound,
  Plus,
  Edit,
  Trash2,
  UserPlus,
  Target,
  AlertCircle,
  Check,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const TeamsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [kpis, setKpis] = useState([]);
  
  // Dialog states
  const [isTeamDialogOpen, setIsTeamDialogOpen] = useState(false);
  const [isMembersDialogOpen, setIsMembersDialogOpen] = useState(false);
  const [isKpisDialogOpen, setIsKpisDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form states
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedKpis, setSelectedKpis] = useState([]);
  const [teamToDelete, setTeamToDelete] = useState(null);
  
  // Action states
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [teamsRes, usersRes, kpisRes] = await Promise.all([
        teamsAPI.getAll(),
        usersAPI.getAll(),
        kpisAPI.getAll(),
      ]);
      setTeams(teamsRes.data);
      setUsers(usersRes.data);
      setKpis(kpisRes.data);
    } catch (error) {
      console.error('Error fetching teams data:', error);
      toast.error('Error al cargar los datos de equipos');
    } finally {
      setLoading(false);
    }
  };

  // Team CRUD handlers
  const handleOpenTeamDialog = (team = null) => {
    if (team) {
      setEditingTeam(team);
      setTeamName(team.name);
    } else {
      setEditingTeam(null);
      setTeamName('');
    }
    setIsTeamDialogOpen(true);
  };

  const handleSaveTeam = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) {
      toast.error('El nombre del equipo es requerido');
      return;
    }
    
    setSaving(true);
    try {
      if (editingTeam) {
        await teamsAPI.update(editingTeam.id, { name: teamName.trim() });
        toast.success('Equipo actualizado');
      } else {
        await teamsAPI.create({ name: teamName.trim() });
        toast.success('Equipo creado');
      }
      setIsTeamDialogOpen(false);
      setEditingTeam(null);
      setTeamName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el equipo');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTeamStatus = async (team) => {
    try {
      await teamsAPI.update(team.id, { is_active: !team.is_active });
      toast.success(team.is_active ? 'Equipo desactivado' : 'Equipo activado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleOpenDeleteDialog = (team) => {
    setTeamToDelete(team);
    setDeleteDialogOpen(true);
  };

  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;
    setDeleting(true);
    try {
      await teamsAPI.delete(teamToDelete.id);
      toast.success('Equipo eliminado');
      setDeleteDialogOpen(false);
      setTeamToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar el equipo');
    } finally {
      setDeleting(false);
    }
  };

  // Members handlers
  const handleOpenMembersDialog = (team) => {
    setSelectedTeam(team);
    setSelectedMembers(team.member_ids || []);
    setIsMembersDialogOpen(true);
  };

  const handleToggleMember = (userId) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSaveMembers = async () => {
    if (!selectedTeam) return;
    setSaving(true);
    try {
      await teamsAPI.updateMembers(selectedTeam.id, selectedMembers);
      toast.success('Miembros actualizados');
      setIsMembersDialogOpen(false);
      setSelectedTeam(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar miembros');
    } finally {
      setSaving(false);
    }
  };

  // KPIs handlers
  const handleOpenKpisDialog = (team) => {
    setSelectedTeam(team);
    setSelectedKpis(team.kpi_ids || []);
    setIsKpisDialogOpen(true);
  };

  const handleToggleKpi = (kpiId) => {
    setSelectedKpis(prev => 
      prev.includes(kpiId) 
        ? prev.filter(id => id !== kpiId)
        : [...prev, kpiId]
    );
  };

  const handleSaveKpis = async () => {
    if (!selectedTeam) return;
    setSaving(true);
    try {
      await teamsAPI.updateKpis(selectedTeam.id, selectedKpis);
      toast.success('KPIs actualizados');
      setIsKpisDialogOpen(false);
      setSelectedTeam(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar KPIs');
    } finally {
      setSaving(false);
    }
  };

  // Get user/kpi names by IDs
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Usuario desconocido';
  };

  const getKpiName = (kpiId) => {
    const kpi = kpis.find(k => k.id === kpiId);
    return kpi?.name || 'KPI desconocido';
  };

  if (loading) {
    return (
      <Card data-testid="teams-settings-card">
        <CardHeader>
          <CardTitle className="font-['Manrope'] flex items-center gap-2">
            <UsersRound className="w-5 h-5" />
            Equipos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="teams-settings-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-['Manrope'] flex items-center gap-2">
              <UsersRound className="w-5 h-5" />
              Equipos
            </CardTitle>
            <CardDescription>
              Gestiona equipos y asigna usuarios/KPIs
            </CardDescription>
          </div>
          <Button onClick={() => handleOpenTeamDialog()} data-testid="add-team-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Equipo
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {teams.length === 0 ? (
            <div className="text-center py-8 sm:py-12 px-4">
              <UsersRound className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm sm:text-base">No hay equipos configurados</p>
              <Button 
                variant="outline" 
                className="mt-4" 
                onClick={() => handleOpenTeamDialog()}
              >
                Crear primer equipo
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {teams.map((team) => (
                <div
                  key={team.id}
                  className={cn(
                    "p-4 hover:bg-slate-50 transition-colors",
                    !team.is_active && "opacity-60"
                  )}
                  data-testid={`team-row-${team.id}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                        team.is_active ? "bg-indigo-100" : "bg-slate-100"
                      )}>
                        <UsersRound className={cn(
                          "w-5 h-5",
                          team.is_active ? "text-indigo-600" : "text-slate-400"
                        )} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900 truncate">{team.name}</h3>
                          {!team.is_active && (
                            <Badge variant="outline" className="text-xs bg-slate-100">
                              Inactivo
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-500 mt-0.5">
                          <span className="flex items-center gap-1">
                            <UserPlus className="w-3 h-3" />
                            {team.member_count} miembros
                          </span>
                          <span className="flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            {team.kpi_count} KPIs
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenMembersDialog(team)}
                        data-testid={`team-members-btn-${team.id}`}
                      >
                        <UserPlus className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Miembros</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenKpisDialog(team)}
                        data-testid={`team-kpis-btn-${team.id}`}
                      >
                        <Target className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">KPIs</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenTeamDialog(team)}
                        data-testid={`team-edit-btn-${team.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Switch
                        checked={team.is_active}
                        onCheckedChange={() => handleToggleTeamStatus(team)}
                        data-testid={`team-toggle-${team.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        onClick={() => handleOpenDeleteDialog(team)}
                        data-testid={`team-delete-btn-${team.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Show members preview */}
                  {team.member_ids?.length > 0 && (
                    <div className="mt-3 pl-13 flex flex-wrap gap-1">
                      {team.member_ids.slice(0, 5).map(userId => (
                        <Badge key={userId} variant="outline" className="text-xs bg-slate-50">
                          {getUserName(userId)}
                        </Badge>
                      ))}
                      {team.member_ids.length > 5 && (
                        <Badge variant="outline" className="text-xs">
                          +{team.member_ids.length - 5} más
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Team Dialog */}
      <Dialog open={isTeamDialogOpen} onOpenChange={setIsTeamDialogOpen}>
        <DialogContent data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingTeam ? 'Editar Equipo' : 'Nuevo Equipo'}
            </DialogTitle>
            <DialogDescription>
              {editingTeam 
                ? 'Modifica el nombre del equipo'
                : 'Crea un nuevo equipo para agrupar usuarios y KPIs'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveTeam}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="team-name">Nombre del Equipo</Label>
                <Input
                  id="team-name"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Ej: Producción Norte"
                  data-testid="team-name-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsTeamDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-team-btn">
                {saving ? 'Guardando...' : editingTeam ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={isMembersDialogOpen} onOpenChange={setIsMembersDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="members-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              Gestionar Miembros
            </DialogTitle>
            <DialogDescription>
              Selecciona los usuarios que pertenecen a <strong>{selectedTeam?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2 max-h-[50vh] overflow-y-auto">
            {users.filter(u => u.is_active).map(user => (
              <label
                key={user.id}
                htmlFor={`member-checkbox-${user.id}`}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors",
                  selectedMembers.includes(user.id) 
                    ? "border-indigo-300 bg-indigo-50" 
                    : "border-slate-200 hover:bg-slate-50"
                )}
                data-testid={`member-option-${user.id}`}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`member-checkbox-${user.id}`}
                    checked={selectedMembers.includes(user.id)}
                    onCheckedChange={() => handleToggleMember(user.id)}
                  />
                  <div>
                    <p className="font-medium text-sm">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-xs">
                  {user.role === 'admin' ? 'Admin' : 'Usuario'}
                </Badge>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
            <span>{selectedMembers.length} seleccionados</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedMembers([])}
              className="text-xs"
            >
              Limpiar
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMembersDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveMembers} disabled={saving} data-testid="save-members-btn">
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KPIs Dialog */}
      <Dialog open={isKpisDialogOpen} onOpenChange={setIsKpisDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="kpis-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              Gestionar KPIs
            </DialogTitle>
            <DialogDescription>
              Selecciona los KPIs que pertenecen a <strong>{selectedTeam?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2 max-h-[50vh] overflow-y-auto">
            {kpis.filter(k => k.is_active).map(kpi => (
              <label
                key={kpi.id}
                htmlFor={`kpi-checkbox-${kpi.id}`}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors",
                  selectedKpis.includes(kpi.id) 
                    ? "border-emerald-300 bg-emerald-50" 
                    : "border-slate-200 hover:bg-slate-50"
                )}
                data-testid={`kpi-option-${kpi.id}`}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`kpi-checkbox-${kpi.id}`}
                    checked={selectedKpis.includes(kpi.id)}
                    onCheckedChange={() => handleToggleKpi(kpi.id)}
                  />
                  <div>
                    <p className="font-medium text-sm">{kpi.name}</p>
                    <p className="text-xs text-slate-500">{kpi.area}</p>
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
            <span>{selectedKpis.length} seleccionados</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedKpis([])}
              className="text-xs"
            >
              Limpiar
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsKpisDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveKpis} disabled={saving} data-testid="save-kpis-btn">
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Team Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-team-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['Manrope'] flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-rose-600" />
              Eliminar Equipo
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                ¿Estás seguro de que deseas eliminar el equipo <strong>{teamToDelete?.name}</strong>?
              </p>
              <p className="text-sm text-slate-500">
                Esta acción no afectará a los usuarios ni KPIs, solo eliminará la agrupación.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTeam}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-team-btn"
            >
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TeamsManagement;
